import { extractFnArgs, createCacheKeyFromArgs } from './utils';
import sorter from './sorter';
import paginator from './paginator';
import Cache from 'utils/cache';

/**
 * Runs data through sorter/paginator/filter
 *
 * @param {Array} data Data to be collated
 * @param {Object} args Collate options
 *
 * @returns {Object|Array} Collated data
 */
function collate(data, args) {
	let collatedData = data;

	if (args.sorting) {
		collatedData = sorter(collatedData, args.sorting);
	}

	//TODO: Add filtering

	if (args.pagination) {
		collatedData = paginator(collatedData, args.pagination);
	}

	return collatedData;
}

/**
 * Creates a new collated function wrapping fn
 *
 * @param {Function} fn Wrapped method to be collated
 *
 * @returns {Function} Function wrapped in a collator function
 */
export function createCollator(fn) {
	const cache = new Cache();

	async function collatorFn(args) {
		/** use fn args to create a key (excluding pagination/sorting) */
		const key = createCacheKeyFromArgs(extractFnArgs(args));
		const clearCache = args.clearCache ? args.clearCache : false;

		let data;
		if (cache.has(key) && !clearCache) {
			data = cache.get(key);
		} else {
			/**
			 * Empty cache because we only want to keep while we
			 * filter the data not across requests
			 */
			cache.empty();
			try {
				data = await fn.call(this, args);
				cache.set(key, data);
			} catch (error) {
				//FIXME: Maybe we should handle this somehow
				throw error;
			}
		}

		return collate(data, args);
	}

	return collatorFn;
}

/**
 * Decorator to add pagination/sorting and filtering to a method
 *
 * About the name:
 * 	 Naming things is hard ... to give a single name for 3 things is even harder
 * 	 Here we found the name: https://english.stackexchange.com/questions/323211/umbrella-term-for-paging-sorting-and-filtering-in-software
 *	 And here what it means: http://www.dictionary.com/browse/collate
 *
 *   If we can think of a better name .. I'll give you 100$
 *
 * This decorator works as follows:
 *
 * It wraps a method which returns a promise and looks for
 * `pagination`, `sorting` and `filters` props in the object param
 * of the method.
 *
 * First call waits for the promise to return and caches the data
 * then runs the data through a sorter then a filter and finally
 * a paginator function. (only if it finds the props)
 *
 * On the second call it uses the cached data with the new collate
 * option to return new sorted/filtered/paginated data.
 *
 * It invalidates the cache when the method params (excluding pagination/sorting/filters)
 * changes.
 *
 * It can do only one (ex. sorting) or all of them.
 *
 * @param {Object} target Class containing the method being decorated
 * @param {string} name Name of the method beeing decorated
 * @param {Object} descriptor Descriptor object of the method
 *
 * @returns {Object} Descriptor with wrapped value in a collator function
 */
export default function collated(target, name, descriptor) {
	descriptor.value = createCollator(descriptor.value);
	return descriptor;
}



// WEBPACK FOOTER //
// ./src/state/helpers/collated/index.js